import VariableService from '@/services/variables';
import { StandardResponse } from '@/types/common';
import type {
  CreateCohortVariablePayload,
  UpdateCohortVariablePayload,
  Variable,
} from '@/types/variables';
import { isCohortVariable } from '@/types/variables';
import { getErrorMessage } from '@/utils/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

export const cohortVariablesKeys = {
  all: ['cohort-variables'] as const,
  lists: () => [...cohortVariablesKeys.all, 'list'] as const,
  list: () => [...cohortVariablesKeys.lists()] as const,
};

const COHORT_VARIABLE_FILTERS = {
  content_type: 'orgs.org',
  include_values: 'true',
  page_size: 100,
};

const getMutationErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;

  if (typeof data?.error === 'string') {
    return data.error;
  }

  const fieldErrors = data?.errors as
    | Record<string, Record<string, string[]>>
    | undefined;
  if (fieldErrors) {
    for (const entry of Object.values(fieldErrors)) {
      const firstField = Object.values(entry)[0];
      if (Array.isArray(firstField) && firstField[0]) {
        return firstField[0];
      }
    }
  }

  return getErrorMessage(error) || fallback;
};

async function fetchCohortVariables(): Promise<StandardResponse<Variable>> {
  const response = await VariableService.getVariables({
    filters: COHORT_VARIABLE_FILTERS,
  });

  return {
    ...response,
    results: (response.results ?? []).filter(isCohortVariable),
  };
}

function normalizeCohortVariable(
  variable: Variable,
  fallback?: Variable
): Variable {
  return {
    ...(fallback ?? {}),
    ...variable,
    props: {
      ...(fallback?.props ?? {}),
      ...(variable.props ?? {}),
      source: 'cohort',
    },
    values: variable.values ?? fallback?.values,
  };
}

export const useCohortVariables = (enabled = true) => {
  return useQuery<StandardResponse<Variable>>({
    queryKey: cohortVariablesKeys.list(),
    queryFn: fetchCohortVariables,
    enabled,
  });
};

export const useCreateCohortVariable = () => {
  const queryClient = useQueryClient();

  return useMutation<Variable[], Error, CreateCohortVariablePayload>({
    mutationFn: (payload) => VariableService.createCohortVariables(payload),
    onSuccess: async (created) => {
      queryClient.setQueryData<StandardResponse<Variable>>(
        cohortVariablesKeys.list(),
        (current) => {
          const normalized = created.map((variable) =>
            normalizeCohortVariable(variable)
          );
          if (!current) {
            return {
              count: normalized.length,
              next: null,
              previous: null,
              results: normalized,
            };
          }

          const existingIds = new Set(current.results.map((variable) => variable.id));
          const appended = normalized.filter(
            (variable) => !existingIds.has(variable.id)
          );

          return {
            ...current,
            count: current.count + appended.length,
            results: [...current.results, ...appended],
          };
        }
      );

      await queryClient.invalidateQueries({
        queryKey: cohortVariablesKeys.all,
        refetchType: 'active',
      });
      toast.success('Cohort variable created successfully');
    },
    onError: (error) => {
      toast.error(getMutationErrorMessage(error, 'Failed to create cohort variable'));
    },
  });
};

export const usePatchCohortVariable = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Variable,
    Error,
    { id: number; data: UpdateCohortVariablePayload }
  >({
    mutationFn: ({ id, data }) => VariableService.updateVariables(id, data),
    onSuccess: async (updated) => {
      queryClient.setQueryData<StandardResponse<Variable>>(
        cohortVariablesKeys.list(),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            results: current.results.map((variable) =>
              variable.id === updated.id
                ? normalizeCohortVariable(updated, variable)
                : variable
            ),
          };
        }
      );

      await queryClient.invalidateQueries({
        queryKey: cohortVariablesKeys.all,
        refetchType: 'active',
      });
      toast.success('Cohort variable updated successfully');
    },
    onError: (error) => {
      toast.error(getMutationErrorMessage(error, 'Failed to update cohort variable'));
    },
  });
};
